import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/ui/password-input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const ROLE_TYPES = [
  { value: 'slp', label: 'SLP' },
  { value: 'special_education_teacher', label: 'Special Education Teacher' },
  { value: 'school_psych', label: 'School Psych' },
  { value: 'ot_pt', label: 'OT/PT' },
  { value: 'admin', label: 'Admin' },
  { value: 'other', label: 'Other' },
];

export const SignupPage = ({ onSwitchToLogin }) => {
  const { signup } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [roleType, setRoleType] = useState('');
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      await signup(email, password, marketingOptIn, roleType || null);
      toast.success('Account created successfully!');
    } catch (err) {
      const message = err.response?.data?.detail || 'Signup failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFCFB] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-sm border border-[#E5E5E5] p-8">
          {/* Logo */}
          <div className="flex items-center justify-center gap-2.5 mb-8">
            <div className="w-10 h-10 rounded-xl bg-[#2C4C3B] flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <h1 className="font-semibold text-xl text-[#1C1C1C]">Rubric Builder</h1>
          </div>

          <h2 className="text-lg font-medium text-center text-[#1C1C1C] mb-2">Create your account</h2>
          <p className="text-sm text-[#595959] text-center mb-6">
            Start building professional rubrics for your students
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@school.edu"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="signup-email-input"
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <PasswordInput
                id="password"
                placeholder="At least 6 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="signup-password-input"
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <PasswordInput
                id="confirmPassword"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                data-testid="signup-confirm-password-input"
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="roleType">Your Role (optional)</Label>
              <Select value={roleType} onValueChange={setRoleType}>
                <SelectTrigger data-testid="role-type-select" className="rounded-xl">
                  <SelectValue placeholder="Select your role" />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_TYPES.map(role => (
                    <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-start space-x-3 pt-2">
              <Checkbox
                id="marketing"
                checked={marketingOptIn}
                onCheckedChange={setMarketingOptIn}
                data-testid="marketing-opt-in-checkbox"
              />
              <div>
                <Label 
                  htmlFor="marketing" 
                  className="text-sm font-normal text-muted-foreground cursor-pointer leading-tight"
                >
                  Send me product updates and new features
                </Label>
                <p className="text-xs text-muted-foreground/70 mt-0.5">
                  1–2 emails/month. Unsubscribe anytime.
                </p>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full rounded-xl"
              disabled={loading}
              data-testid="signup-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account?{' '}
            <button
              onClick={onSwitchToLogin}
              className="text-primary hover:underline font-medium"
              data-testid="switch-to-login-btn"
            >
              Sign in
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
